// Plan 08-04 / VISUAL-PASS v2 — PieceLayer: crisp procedural "Glass-Rune Court"
// vector pieces (no atlas sprites, no big romb).
//
// REQ-accessibility-baseline (UI-SPEC §"Accessibility Contract"): player
// identity is encoded via THREE redundant channels:
//   1. SHAPE     — slot 0 stands on a HEX foot, slot 1 on a DIAMOND foot.
//   2. POSITION  — your pieces at screen bottom, opponent at top (cellIdxToScreen).
//   3. COLOR     — slot 0 = --color-player-a, slot 1 = --color-player-b.
//
// VISUAL-PASS v2 (replaces the upscaled-32px atlas sprites that read blurry +
// the ~full-cell hex/diamond "romb"): each piece is a procedural Pixi v8
// Graphics token drawn at NATIVE 64px so it stays crisp. Recipe per piece:
//   contact shadow → 12-direction Inkwell dilation outline (uniform sticker
//   edge that no curve can break) → dark player-color body+foot → glow-color
//   gloss inset → Bone catch-light → role glyph/detail.
// The hated romb is replaced by a slim faceted FOOT (hex/diamond, ~0.18 radius)
// — the piece dominates; the foot is a shoe. The unassigned piece is "The Sworn
// Stone": a rune-monolith with a dormant rune (lights on empower). All colours
// come from theme tokens + universal Inkwell/Bone/Sun-Yellow (no hardcoded hex).

import { useStore } from '@nanostores/react'
import type { JSX } from 'react'
import { useRef } from 'react'
import { Group } from 'tweedle.js'
import { $reducedMotion } from '../../stores/motion'
import { actionTokens, hexToInt, themeTokens } from '../../styles/tokens'
import type { PlayerView } from '../PlayerViewSchema'
import { PieceSpriteMount } from '../sprites/PieceSpriteMount'
import { resolveChessGrid } from '../wire-grids'
import { CELL_SIZE_PX, CHESS_ROWS, cellIdxToScreen } from './board-layout'
import {
  pieceColorForSlot,
  pieceGlowForSlot,
  pieceShapeForSlot,
  selectRenderablePieces,
} from './piece-identity'

const C = CELL_SIZE_PX
const INK = hexToInt(actionTokens.inkwell)
// Folk reskin: BONE is the LIGHT enamel catch-light / jewel / spark / lit-edge
// color. textPrimary is now dark ink, so BONE sources from the LIGHT onAction
// (white) — pieces read as colored enamel with white highlights + dark INK
// outline on the cream board.
const BONE = hexToInt(actionTokens.onAction)

type Fill = number | { color: number; alpha?: number }

/** Minimal Pixi v8 Graphics surface we use (keeps the draw callbacks typed). */
export interface G {
  clear: () => unknown
  moveTo: (x: number, y: number) => unknown
  lineTo: (x: number, y: number) => unknown
  quadraticCurveTo: (cx: number, cy: number, x: number, y: number) => unknown
  closePath: () => unknown
  circle: (x: number, y: number, r: number) => unknown
  ellipse: (x: number, y: number, rx: number, ry: number) => unknown
  roundRect: (x: number, y: number, w: number, h: number, r: number) => unknown
  poly: (points: number[]) => unknown
  fill: (style: Fill) => unknown
  stroke: (style: {
    width: number
    color: number
    alpha?: number
    cap?: 'round' | 'butt' | 'square'
    join?: 'round' | 'bevel' | 'miter'
  }) => unknown
}

// --- path-command mini-DSL (normalized 0..1 coords; scaled ×C at exec) -------
type Cmd =
  | ['M', number, number]
  | ['L', number, number]
  | ['Q', number, number, number, number]
  | ['Z']

function execPath(g: G, cmds: readonly Cmd[], ox: number, oy: number): void {
  for (const c of cmds) {
    if (c[0] === 'M') g.moveTo(c[1] * C + ox, c[2] * C + oy)
    else if (c[0] === 'L') g.lineTo(c[1] * C + ox, c[2] * C + oy)
    else if (c[0] === 'Q')
      g.quadraticCurveTo(
        c[1] * C + ox,
        c[2] * C + oy,
        c[3] * C + ox,
        c[4] * C + oy,
      )
    else g.closePath()
  }
}

function fillPath(
  g: G,
  cmds: readonly Cmd[],
  color: Fill,
  ox = 0,
  oy = 0,
): void {
  execPath(g, cmds, ox, oy)
  g.fill(color)
}

function fillPoly(
  g: G,
  pts: readonly number[],
  color: Fill,
  ox = 0,
  oy = 0,
): void {
  const p: number[] = []
  for (let i = 0; i < pts.length; i += 2) {
    p.push((pts[i] ?? 0) * C + ox, (pts[i + 1] ?? 0) * C + oy)
  }
  g.poly(p)
  g.fill(color)
}

function fillCirc(
  g: G,
  x: number,
  y: number,
  r: number,
  color: Fill,
  ox = 0,
  oy = 0,
): void {
  g.circle(x * C + ox, y * C + oy, r * C)
  g.fill(color)
}

/** Scale a path about (px,py) — used for the glow gloss inset. */
function scaleCmds(
  cmds: readonly Cmd[],
  f: number,
  px: number,
  py: number,
): Cmd[] {
  const sx = (x: number) => px + (x - px) * f
  const sy = (y: number) => py + (y - py) * f
  return cmds.map((c) => {
    if (c[0] === 'M') return ['M', sx(c[1]), sy(c[2])]
    if (c[0] === 'L') return ['L', sx(c[1]), sy(c[2])]
    if (c[0] === 'Q') return ['Q', sx(c[1]), sy(c[2]), sx(c[3]), sy(c[4])]
    return ['Z']
  })
}

// --- shared geometry --------------------------------------------------------
const HEX_FOOT = [
  0.34, 0.875, 0.41, 0.81, 0.59, 0.81, 0.66, 0.875, 0.59, 0.94, 0.41, 0.94,
]
const DIAMOND_FOOT = [0.5, 0.81, 0.68, 0.875, 0.5, 0.94, 0.32, 0.875]
const HEX_LIP = [0.36, 0.86, 0.42, 0.815, 0.58, 0.815, 0.64, 0.86]
const DIAMOND_LIP = [0.5, 0.815, 0.66, 0.875, 0.34, 0.875]

// 12-direction dilation offsets for the uniform Inkwell outline.
const OUTLINE_R = 0.038 * C
const DIRS: ReadonlyArray<[number, number]> = Array.from(
  { length: 16 },
  (_, i) => {
    const a = (i * Math.PI) / 8
    return [Math.cos(a) * OUTLINE_R, Math.sin(a) * OUTLINE_R]
  },
)

export type Kind = 'king' | 'queen' | 'knight' | 'bishop' | 'rook' | 'blank'

// --- per-piece silhouette body paths (the outlined token) -------------------
const BODY: Record<Kind, readonly Cmd[]> = {
  // King — BROAD, planted, squared shoulders, near-vertical sides (the anchor).
  king: [
    ['M', 0.25, 0.85],
    ['L', 0.27, 0.5],
    ['Q', 0.27, 0.41, 0.37, 0.385],
    ['Q', 0.5, 0.37, 0.63, 0.385],
    ['Q', 0.73, 0.41, 0.73, 0.5],
    ['L', 0.75, 0.85],
    ['Z'],
  ],
  // Queen — TALL hourglass: wide hem, pinched waist, flared bust (vs the king).
  queen: [
    ['M', 0.31, 0.85],
    ['L', 0.385, 0.6],
    ['Q', 0.355, 0.5, 0.405, 0.46],
    ['Q', 0.43, 0.43, 0.435, 0.4],
    ['Q', 0.5, 0.375, 0.565, 0.4],
    ['Q', 0.57, 0.43, 0.595, 0.46],
    ['Q', 0.645, 0.5, 0.615, 0.6],
    ['L', 0.69, 0.85],
    ['Z'],
  ],
  // Knight — horse-head profile (benchmark): extended muzzle + sharper ear.
  knight: [
    ['M', 0.33, 0.85],
    ['L', 0.36, 0.55],
    ['Q', 0.355, 0.42, 0.41, 0.36],
    ['Q', 0.45, 0.3, 0.4, 0.25],
    ['L', 0.44, 0.18],
    ['Q', 0.52, 0.215, 0.59, 0.235],
    ['Q', 0.72, 0.26, 0.76, 0.38],
    ['Q', 0.78, 0.45, 0.68, 0.47],
    ['Q', 0.62, 0.48, 0.6, 0.53],
    ['L', 0.64, 0.56],
    ['L', 0.67, 0.85],
    ['Z'],
  ],
  // Bishop — SLIM, leaning-slender mitre cone.
  bishop: [
    ['M', 0.37, 0.85],
    ['L', 0.395, 0.55],
    ['Q', 0.38, 0.46, 0.42, 0.41],
    ['Q', 0.43, 0.3, 0.5, 0.155],
    ['Q', 0.57, 0.3, 0.58, 0.41],
    ['Q', 0.62, 0.46, 0.605, 0.55],
    ['L', 0.63, 0.85],
    ['Z'],
  ],
  // Rook — COLUMNAR tower (near-vertical walls, no bottle flare).
  rook: [
    ['M', 0.345, 0.85],
    ['L', 0.35, 0.42],
    ['Q', 0.35, 0.38, 0.38, 0.36],
    ['L', 0.375, 0.3],
    ['L', 0.625, 0.3],
    ['L', 0.62, 0.36],
    ['Q', 0.65, 0.38, 0.65, 0.42],
    ['L', 0.655, 0.85],
    ['Z'],
  ],
  // Sworn Stone — ANGULAR monolith: chamfered flat cap, straight tapering sides.
  blank: [
    ['M', 0.32, 0.27],
    ['L', 0.41, 0.215],
    ['L', 0.59, 0.215],
    ['L', 0.68, 0.27],
    ['L', 0.71, 0.85],
    ['L', 0.29, 0.85],
    ['Z'],
  ],
}

// Heads (circles fused into the silhouette) — kinds without an entry have none.
const HEAD: Partial<Record<Kind, [number, number, number]>> = {
  king: [0.5, 0.315, 0.13],
  queen: [0.5, 0.33, 0.092],
}

// King 3-point coronet (central point tallest) — fused to the head, outlined.
const KING_CROWN = [
  0.36, 0.315, 0.385, 0.225, 0.435, 0.285, 0.5, 0.16, 0.565, 0.285, 0.615,
  0.225, 0.64, 0.315,
]
// Queen tiara — taller, wider, thicker-based spikes so the outline fuses them.
const QUEEN_TIARA = [
  0.345, 0.28, 0.375, 0.16, 0.41, 0.245, 0.45, 0.13, 0.475, 0.235, 0.5, 0.095,
  0.525, 0.235, 0.55, 0.13, 0.59, 0.245, 0.625, 0.16, 0.655, 0.28,
]
const ROOK_MERLONS = [
  0.355, 0.3, 0.355, 0.225, 0.42, 0.225, 0.42, 0.27, 0.465, 0.27, 0.465, 0.225,
  0.535, 0.225, 0.535, 0.27, 0.58, 0.27, 0.58, 0.225, 0.645, 0.225, 0.645, 0.3,
]

/** Draw the full outlined SILHOUETTE (foot + body + head + protruding features)
 *  in one colour, offset by (ox,oy). Used for both the Inkwell dilation outline
 *  and the player-colour body fill. */
function drawSilhouette(
  g: G,
  kind: Kind,
  slot: 0 | 1,
  color: Fill,
  ox: number,
  oy: number,
): void {
  fillPoly(g, slot === 0 ? HEX_FOOT : DIAMOND_FOOT, color, ox, oy)
  fillPath(g, BODY[kind], color, ox, oy)
  const head = HEAD[kind]
  if (head) fillCirc(g, head[0], head[1], head[2], color, ox, oy)
  if (kind === 'king') {
    fillPoly(g, KING_CROWN, color, ox, oy) // 3-point coronet (outlined)
    fillCirc(g, 0.5, 0.135, 0.05, color, ox, oy) // orb finial on central point
  }
  if (kind === 'queen') fillPoly(g, QUEEN_TIARA, color, ox, oy)
  if (kind === 'bishop') fillCirc(g, 0.5, 0.145, 0.038, color, ox, oy) // tip bead
  if (kind === 'rook') fillPoly(g, ROOK_MERLONS, color, ox, oy)
}

/** Draw the coloured accents (gloss, recoloured features, catch-light, glyphs)
 *  ON TOP of the body — these live inside the silhouette so need no outline. */
function drawAccents(g: G, kind: Kind, slot: 0 | 1, glow: number): void {
  const GLOSS_SOFT: Fill = { color: glow, alpha: 0.5 }
  const GLOSS_BRIGHT: Fill = { color: glow, alpha: 0.92 }
  const LIGHT: Fill = { color: glow, alpha: 0.7 }
  const SPARK: Fill = { color: BONE, alpha: 0.78 }

  // Foot lip bevel.
  fillPoly(g, slot === 0 ? HEX_LIP : DIAMOND_LIP, LIGHT)

  // Graduated gloss: a soft upper band + a smaller bright core → faked enamel
  // gradient (Pixi v8 Graphics can't gradient-fill cleanly at this scale).
  fillPath(g, scaleCmds(BODY[kind], 0.88, 0.5, 0.46), GLOSS_SOFT)
  fillPath(g, scaleCmds(BODY[kind], 0.6, 0.5, 0.4), GLOSS_BRIGHT)
  const head = HEAD[kind]
  if (head) {
    fillCirc(g, head[0], head[1] - 0.012, head[2] * 0.82, GLOSS_SOFT)
    fillCirc(g, head[0] - 0.03, head[1] - 0.035, head[2] * 0.4, SPARK) // catch-light
  }

  switch (kind) {
    case 'king':
      fillPoly(g, KING_CROWN, { color: glow, alpha: 0.85 }) // lit coronet
      fillCirc(g, 0.385, 0.225, 0.018, SPARK) // jeweled tips
      fillCirc(g, 0.615, 0.225, 0.018, SPARK)
      fillCirc(g, 0.5, 0.135, 0.05, { color: glow }) // orb finial
      fillCirc(g, 0.482, 0.122, 0.018, SPARK) // orb catch
      fillRoundRect(g, 0.482, 0.052, 0.036, 0.088, 0.012, { color: INK }) // cross-V
      fillRoundRect(g, 0.452, 0.076, 0.096, 0.03, 0.01, { color: INK }) // cross-H
      break
    case 'queen':
      fillPoly(g, QUEEN_TIARA, { color: glow })
      for (const [jx, jy] of [
        [0.5, 0.095],
        [0.45, 0.13],
        [0.55, 0.13],
      ] as ReadonlyArray<[number, number]>) {
        fillCirc(g, jx, jy, 0.034, { color: INK, alpha: 0.5 }) // socket
        fillCirc(g, jx, jy, 0.026, { color: BONE, alpha: 0.92 }) // jewel
      }
      fillPoly(g, [0.5, 0.5, 0.555, 0.57, 0.5, 0.64, 0.445, 0.57], {
        color: glow,
      }) // chest gem
      fillCirc(g, 0.5, 0.555, 0.018, SPARK)
      break
    case 'knight':
      // Mane clamped INSIDE the body's left edge so it can't bleed past outline.
      fillPoly(
        g,
        [
          0.43, 0.24, 0.405, 0.3, 0.435, 0.35, 0.405, 0.41, 0.44, 0.46, 0.415,
          0.52, 0.45, 0.5,
        ],
        { color: glow, alpha: 0.92 },
      )
      fillCirc(g, 0.58, 0.345, 0.028, { color: INK }) // eye
      fillCirc(g, 0.715, 0.41, 0.018, { color: INK, alpha: 0.75 }) // nostril
      break
    case 'bishop':
      fillCirc(g, 0.5, 0.155, 0.04, { color: glow }) // tip bead
      fillCirc(g, 0.486, 0.145, 0.014, SPARK)
      strokeLine(g, 0.47, 0.45, 0.55, 0.37, 0.022, INK, 0.7) // interior slit
      break
    case 'rook':
      fillRoundRect(g, 0.475, 0.5, 0.05, 0.18, 0.012, { color: INK }) // arrow-slit
      fillRoundRect(g, 0.36, 0.4, 0.28, 0.03, 0.01, { color: glow, alpha: 0.5 }) // parapet seam
      break
    case 'blank':
      strokeLine(g, 0.5, 0.235, 0.5, 0.83, 0.016, INK, 0.28) // facet seam
      fillRoundRect(g, 0.355, 0.3, 0.045, 0.22, 0.02, {
        color: BONE,
        alpha: 0.7,
      }) // lit edge
      // DORMANT rune: cold inner fill + engraved INK outline + Bone edge-catch.
      fillPoly(g, [0.5, 0.45, 0.575, 0.52, 0.5, 0.59, 0.425, 0.52], {
        color: glow,
        alpha: 0.3,
      })
      strokeLine(g, 0.5, 0.45, 0.575, 0.52, 0.016, INK, 0.85)
      strokeLine(g, 0.575, 0.52, 0.5, 0.59, 0.016, INK, 0.85)
      strokeLine(g, 0.5, 0.59, 0.425, 0.52, 0.016, INK, 0.85)
      strokeLine(g, 0.425, 0.52, 0.5, 0.45, 0.016, INK, 0.85)
      strokeLine(g, 0.46, 0.485, 0.5, 0.45, 0.012, BONE, 0.6)
      break
  }
}

function fillRoundRect(
  g: G,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  color: Fill,
): void {
  g.roundRect(x * C, y * C, w * C, h * C, r * C)
  g.fill(color)
}

function strokeLine(
  g: G,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  w: number,
  color: number = INK,
  alpha = 1,
): void {
  g.moveTo(x0 * C, y0 * C)
  g.lineTo(x1 * C, y1 * C)
  g.stroke({ width: w * C, color, alpha, cap: 'round', join: 'round' })
}

/** Soft magic aura under an empowered piece (concentric fading ellipses). */
function drawAura(g: G, magic: number): void {
  for (const [ry, a] of [
    [0.14, 0.18],
    [0.1, 0.22],
    [0.06, 0.3],
  ] as const) {
    g.ellipse(0.5 * C, 0.84 * C, 0.32 * C, ry * C)
    g.fill({ color: magic, alpha: a })
  }
}

/** Full piece token: shadow → [aura] → outline → body → accents. */
export function drawPiece(
  g: G,
  kind: Kind,
  slot: 0 | 1,
  fill: number,
  glow: number,
  magic: number,
  empowered: boolean,
): void {
  g.clear()
  if (empowered) drawAura(g, magic)
  // contact shadow: wide soft penumbra + tight dark core, offset down-right to
  // match the top-left key light.
  g.ellipse(0.52 * C, 0.935 * C, 0.31 * C, 0.06 * C)
  g.fill({ color: INK, alpha: 0.16 })
  g.ellipse(0.51 * C, 0.928 * C, 0.21 * C, 0.042 * C)
  g.fill({ color: INK, alpha: 0.32 })
  // uniform Inkwell outline via 12-direction dilation
  for (const [dx, dy] of DIRS) drawSilhouette(g, kind, slot, INK, dx, dy)
  // dark player-colour body + foot
  drawSilhouette(g, kind, slot, fill, 0, 0)
  // gloss + features + catch-light + glyphs
  drawAccents(g, kind, slot, glow)
}

interface PieceLayerProps {
  playerView: PlayerView
}

const AURA_ROLES = new Set(['knight', 'bishop', 'rook', 'queen'])

export function PieceLayer({ playerView }: PieceLayerProps): JSX.Element {
  // WS0b (ARCA-87): piece colours come from the static take-two token set —
  // the runtime theme descriptor / css_vars swap was removed. The pure
  // `piece*ForSlot` helpers keep a cssVars override seam (exercised by
  // PieceLayer.identity.test.ts); the live layer always uses the static path.
  const fillA = hexToInt(pieceColorForSlot(0, null))
  const fillB = hexToInt(pieceColorForSlot(1, null))
  const glowA = hexToInt(pieceGlowForSlot(0, null))
  const glowB = hexToInt(pieceGlowForSlot(1, null))
  const magic = hexToInt(themeTokens.magic)
  const reducedMotion = useStore($reducedMotion)

  // Shared tweedle Group — uses Group.shared because installTweedleTickerDriver
  // ticks only Group.shared; a private Group would produce dead tweens.
  const tweenGroupRef = useRef<Group>(Group.shared)

  // ARCA-749 (WS3) — pieces render off the FIXED 6×6 chess grid, NOT the
  // (growing) combined board. The chess grid is exactly 36 cells / 6 rows and
  // never grows or re-flips on a Flop/River reveal, so the vertical flip uses
  // the constant CHESS_ROWS rather than the live board length.
  const rows = CHESS_ROWS
  const pieces = selectRenderablePieces(resolveChessGrid(playerView))

  return (
    <pixiContainer label="PieceLayer">
      {pieces.map((p) => {
        const { screen_x, screen_y } = cellIdxToScreen(
          p.cellIdx,
          playerView.your_slot,
          rows,
        )
        const baseX = screen_x * CELL_SIZE_PX
        const baseY = screen_y * CELL_SIZE_PX
        const fill = p.ownerSlot === 0 ? fillA : fillB
        const glow = p.ownerSlot === 0 ? glowA : glowB
        const kind: Kind = (p.roleKind ?? p.pieceKind ?? 'blank') as Kind
        const empowered = p.roleKind != null && AURA_ROLES.has(p.roleKind)
        const shape = pieceShapeForSlot(p.ownerSlot)

        return (
          <PieceSpriteMount
            key={`piece-${p.cellIdx}`}
            label={`piece-${p.cellIdx}-slot${p.ownerSlot}-${shape}`}
            kind={kind}
            ownerSlot={p.ownerSlot}
            fill={fill}
            glow={glow}
            magic={magic}
            empowered={empowered}
            x={baseX}
            y={baseY}
            reducedMotion={reducedMotion}
            tweenGroup={tweenGroupRef.current}
          />
        )
      })}
    </pixiContainer>
  )
}
